import { useState } from "react";
import { Plus, Copy, Trash2, FileText, Layers as LayersIcon, BookOpen, Type } from "lucide-react";
import { cn } from "../../../lib/utils";

const TABS = [
  { id: "pages", label: "Pages", icon: FileText },
  { id: "masters", label: "Masters", icon: BookOpen },
  { id: "layers", label: "Layers", icon: LayersIcon },
  { id: "styles", label: "Styles", icon: Type },
];

export default function LeftDockPanels({ collapsed, pageCount = 24, activePage = 1, onPageClick }) {
  const [tab, setTab] = useState("pages");
  if (collapsed) return null;

  return (
    <aside
      data-testid="editor-dock-panels"
      className="w-[220px] shrink-0 border-r border-[var(--pl-border)] bg-[var(--pl-surface)]/55 backdrop-blur-md flex flex-col"
      style={{ height: "100%" }}
    >
      <div className="flex border-b border-[var(--pl-border)]">
        {TABS.map((t) => {
          const I = t.icon;
          const Active = tab === t.id;
          return (
            <button
              key={t.id}
              data-testid={`dock-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={cn(
                "flex-1 h-9 grid place-items-center transition relative",
                Active ? "text-[var(--pl-aqua)]" : "text-[var(--pl-text-muted)] hover:text-[var(--pl-text)]"
              )}
              title={t.label}
            >
              <I className="h-4 w-4" />
              {Active && <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-[var(--pl-aqua)] rounded-full" />}
            </button>
          );
        })}
      </div>

      <div className="flex-1 overflow-y-auto p-3 pl-noscroll">
        {tab === "pages" && <PagesPanel pageCount={pageCount} activePage={activePage} onPageClick={onPageClick} />}
        {tab === "masters" && <MastersPanel />}
        {tab === "layers" && <LayersPanel />}
        {tab === "styles" && <StylesPanel />}
      </div>
    </aside>
  );
}

function PagesPanel({ pageCount, activePage = 1, onPageClick }) {
  const pages = Array.from({ length: pageCount }, (_, i) => i + 1);
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)]">Pages · {pageCount}</div>
        <div className="flex gap-1">
          <button title="Add page" className="h-6 w-6 grid place-items-center rounded-md hover:bg-white/5 text-[var(--pl-text-muted)] hover:text-[var(--pl-aqua)]"><Plus className="h-3.5 w-3.5" /></button>
          <button title="Duplicate page" className="h-6 w-6 grid place-items-center rounded-md hover:bg-white/5 text-[var(--pl-text-muted)] hover:text-[var(--pl-aqua)]"><Copy className="h-3.5 w-3.5" /></button>
          <button title="Delete page" className="h-6 w-6 grid place-items-center rounded-md hover:bg-white/5 text-[var(--pl-text-muted)] hover:text-rose-400"><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {pages.map((p) => {
          const isActive = p === activePage;
          return (
            <button
              type="button"
              key={p}
              data-testid={`page-thumb-${p}`}
              onClick={() => onPageClick && onPageClick(p)}
              className={cn(
                "rounded-md border bg-white text-[8px] text-[#071c1c] aspect-[6/9] grid place-items-end justify-items-center p-1 transition",
                isActive ? "border-[var(--pl-aqua)] ring-2 ring-[var(--pl-aqua)]/40" : "border-[var(--pl-border)] hover:border-[var(--pl-aqua)]/60"
              )}
            >
              <div className="font-mono">{p}</div>
            </button>
          );
        })}
      </div>
      <div className="mt-3 text-[10px] text-[var(--pl-text-muted)] flex items-center gap-1.5">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Text flow connected
      </div>
    </div>
  );
}

function MastersPanel() {
  const masters = [
    { id: "a", label: "A · Body Page", desc: "Default with header & page number" },
    { id: "b", label: "B · Chapter Opener", desc: "Big title, no header" },
    { id: "c", label: "C · Workbook Page", desc: "Lined fill-in template" },
  ];
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)] mb-2">Master pages</div>
      <div className="space-y-1.5">
        {masters.map((m) => (
          <div key={m.id} data-testid={`master-${m.id}`} className="rounded-lg p-2 border border-[var(--pl-border)] bg-white/[0.03]">
            <div className="flex items-center justify-between">
              <div className="text-xs font-medium text-[var(--pl-text)]">{m.label}</div>
              <button className="text-[10px] text-[var(--pl-aqua)]">Apply</button>
            </div>
            <div className="text-[10px] text-[var(--pl-text-muted)] mt-0.5">{m.desc}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function LayersPanel() {
  const layers = ["Header & Footer", "Text Layer", "Image Layer", "Background"];
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)] mb-2">Layers</div>
      <ul className="space-y-1">
        {layers.map((l, i) => (
          <li key={l} data-testid={`layer-${i}`} className="flex items-center justify-between text-xs text-[var(--pl-text)] rounded-md px-2 py-1.5 hover:bg-white/5">
            <span>{l}</span>
            <span className="text-[10px] text-[var(--pl-text-muted)]">visible</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function StylesPanel() {
  const styles = [
    "Header Part", "Header Chapter", "Header 1", "Header 2", "Body Text",
    "Body Text Mini", "Body Text Indent", "Bullet Point", "Bullet Mini", "TOC Level 1",
  ];
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)] mb-2">Paragraph styles</div>
      <ul className="space-y-1">
        {styles.map((s) => (
          <li key={s} className="text-xs text-[var(--pl-text)] rounded-md px-2 py-1.5 hover:bg-[var(--pl-aqua)]/10 hover:text-[var(--pl-aqua)] cursor-pointer">
            {s}
          </li>
        ))}
      </ul>
    </div>
  );
}
